import { NavigateFunction, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import { navigate } from './navigate';
import { GlobalQueryParams, QueryService } from './queryParams.interface';
import { NavigationStates, useAltNavStates } from './useAltNavStates';

// merge queryParams with defaults or what was there last time user visited. (You could instead include params and queryParams in all navigation links.)
export const useQuerySync = <
  QueryParams extends Record<string, any> & GlobalQueryParams,
>(
  queryService: QueryService<QueryParams>,
): void => {
  const navigator: NavigateFunction = useNavigate();

  const navigationState = useAltNavStates<QueryParams>(queryService);

  useEffect(() => {
    if (!navigationState || isEmpty(navigationState.params)) {
      return;
    }

    switch (navigationState.type) {
      case NavigationStates.firstLoad:
        // navigate in case there are defaults merged so they are communicated in the url for reloading or when pressing back button.
        navigate(navigator, navigationState.path, navigationState.params, true);
        // TODO: make sure this one is only called once
        queryService.dispatchQueryParams(navigationState.params);
        break;
      case NavigationStates.noQuery:
        navigate(navigator, navigationState.path, navigationState.params, true);
        break;
      case NavigationStates.queryIsNotPrevQuery:
        // no need to navigate since queryParams have the latest change
        queryService.dispatchQueryParams(navigationState.params);
        break;
      case NavigationStates.storeIsNotPrevStore:
        // no need to set store since storeParams have the latest change.
        // replace is set to false so we can go back to previous queryParams with back and forward buttons.
        navigate(navigator, navigationState.path, navigationState.params);
        break;

      default:
        // eslint-disable-next-line no-console
        console.error(
          'navigation.type needs to be one of the valid NavigationStates',
        );
        break;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigationState]);
};

// function usePrevious<T>(value: T): T {
//   // The ref object is a generic container whose current property is mutable ...
//   // ... and can hold any value, similar to an instance property on a class
//   const ref = useRef<T>();
//   // Store current value in ref
//   useEffect(() => {
//     ref.current = value;
//   }, [value]); // Only re-run if value changes
//   // Return previous value (happens before update in useEffect above)
//   return ref.current as T;
// }
